import { ArtButton, ArtTextField, confirmDialog, useApiService } from "@art/shared-ui";
import { faArrowLeft, faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid2 as Grid,
    IconButton,
    Link,
    MenuItem,
    Paper,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    FormatBold,
    FormatItalic,
    FormatUnderlined,
    FormatListBulleted,
    FormatListNumbered,
    Link as LinkIcon,
    FormatClear,
    SmartButton,
    Image as ImageIcon,
    AlternateEmail,
} from "@mui/icons-material";
import { usePageWrapper } from "../../../hooks";
import { renderTemplate } from "../../../utils/template-utils";
import { getTypeMeta, isTemplateType } from "./template-types";

// A template variable as returned by the API (resolved from the DB).
interface EditorVariable {
    name: string;
    label?: string;
    sample?: string;
    is_url?: boolean;
}

const pillHtml = (name: string, label: string) =>
    `<span data-var="${name}" contenteditable="false" style="display:inline-block;background:#e8f0fe;color:#1a73e8;border:1px solid #c6dafc;border-radius:12px;padding:0 8px;font-size:12px;line-height:20px;vertical-align:baseline;user-select:none;">${label || name}</span>`;

const tokensToPills = (html: string, labels: Record<string, string>) =>
    html.replace(/\{\{\.(\w+)\}\}/g, (_m, n) => pillHtml(n, labels[n] ?? n));

const pillsToTokens = (html: string) =>
    html.replace(/<span[^>]*data-var="(\w+)"[^>]*>.*?<\/span>/g, "{{.$1}}");


const BUTTON_SIZES: Record<string, { padding: string; fontSize: string; label: string }> = {
    sm: { padding: "6px 14px", fontSize: "12px", label: "Small" },
    md: { padding: "10px 22px", fontSize: "14px", label: "Medium" },
    lg: { padding: "14px 32px", fontSize: "17px", label: "Large" },
};

const BUTTON_COLORS = [
    { value: "#4f46e5", label: "Indigo" },
    { value: "#1a73e8", label: "Blue" },
    { value: "#137333", label: "Green" },
    { value: "#c5221f", label: "Red" },
    { value: "#b06000", label: "Orange" },
    { value: "#202124", label: "Black" },
];

const buttonStyle = (size: string, color: string) => {
    const s = BUTTON_SIZES[size] ?? BUTTON_SIZES.md;
    return `display:inline-block;background:${color};color:#ffffff;padding:${s.padding};font-size:${s.fontSize};border-radius:6px;text-decoration:none;font-weight:bold;`;
};

const buttonHtml = (label: string, url: string, size: string, color: string) =>
    `<a href="${url}" data-btn="1" data-size="${size}" data-color="${color}" style="${buttonStyle(size, color)}">${label}</a>&nbsp;`;

function EmailTemplateEditor() {
    const navigate = useNavigate();
    const location = useLocation();
    const { projectKey = "", type = "" } = useParams();
    const apiService = useApiService();

    const projectName = (location.state as { projectName?: string } | null)?.projectName || projectKey;
    const templatesPath = `/application-settings/email-config/${projectKey}/templates`;

    // Card label/description are UI metadata (not stored in the DB).
    const meta = getTypeMeta(type);
    const label = meta.label;
    const description = meta.description;

    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [variables, setVariables] = useState<EditorVariable[]>([]);
    const [isCustom, setIsCustom] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const pillLabels = useMemo(
        () => Object.fromEntries(variables.map((v) => [v.name, v.label || v.name])),
        [variables]
    );
    const insertableVars = variables.filter((v) => !v.is_url);
    const urlVars = variables.filter((v) => v.is_url);
    const previewVars = variables.map((v) => ({ name: v.name, sample: v.sample || "" }));

    const [atMenu, setAtMenu] = useState<{ top: number; left: number } | null>(null);

    const [btnDialogOpen, setBtnDialogOpen] = useState(false);
    const [btnLabel, setBtnLabel] = useState("Click Here");
    const [btnUrl, setBtnUrl] = useState("https://");
    const [btnSize, setBtnSize] = useState("md");
    const [btnColor, setBtnColor] = useState("#4f46e5");
    const editingBtnRef = useRef<HTMLAnchorElement | null>(null);
    const savedRangeRef = useRef<Range | null>(null);

    const editorRef = useRef<HTMLDivElement | null>(null);
    const editorWrapRef = useRef<HTMLDivElement | null>(null);
    const subjectRef = useRef<HTMLInputElement | null>(null);
    const [lastFocused, setLastFocused] = useState<"subject" | "body">("body");

    // Load the resolved template (project override or seeded default) from the
    // API. No local defaults — the DB is the source of truth.
    const loadTemplate = async () => {
        setLoading(true);
        try {
            const response = await apiService.get(
                `/notification/email-template?project_key=${encodeURIComponent(projectKey)}&type=${encodeURIComponent(type)}`
            );
            const tpl = response?.data?.data?.email_template;
            if (!tpl) throw new Error("Template not found");
            const vars: EditorVariable[] = tpl.variables ?? [];
            setSubject(tpl.subject ?? "");
            setBody(tpl.html_body ?? "");
            setVariables(vars);
            setIsCustom(!!tpl.is_custom);
            const labels = Object.fromEntries(vars.map((v) => [v.name, v.label || v.name]));
            if (editorRef.current) {
                editorRef.current.innerHTML = tokensToPills(tpl.html_body ?? "", labels);
            }
        } catch (error: any) {
            apiService.showSnack(
                error?.response?.data?.message || "Failed to load template",
                "error"
            );
            navigate(templatesPath, { state: { projectName } });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!isTemplateType(type)) {
            apiService.showSnack("Unknown template type", "error");
            navigate(templatesPath, { state: { projectName } });
            return;
        }
        loadTemplate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectKey, type]);

    const preview = renderTemplate(subject || "(no subject)", body, previewVars);

    const syncBody = () => {
        if (editorRef.current) setBody(pillsToTokens(editorRef.current.innerHTML));
    };

    const checkAtTrigger = () => {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) return setAtMenu(null);
        const range = sel.getRangeAt(0);
        const node = range.startContainer;
        if (node.nodeType !== Node.TEXT_NODE || range.startOffset === 0) return setAtMenu(null);
        const charBefore = node.textContent?.[range.startOffset - 1];
        if (charBefore !== "@") return setAtMenu(null);
        const rect = range.getBoundingClientRect();
        const wrapRect = editorWrapRef.current?.getBoundingClientRect();
        if (!wrapRect) return;
        setAtMenu({ top: rect.bottom - wrapRect.top + 4, left: rect.left - wrapRect.left });
    };

    const handleEditorInput = () => {
        syncBody();
        checkAtTrigger();
    };

    const format = (command: string, value?: string) => {
        editorRef.current?.focus();
        document.execCommand(command, false, value);
        syncBody();
    };

    const insertHtmlAtCaret = (html: string) => {
        editorRef.current?.focus();
        document.execCommand("insertHTML", false, html);
        syncBody();
    };

    const addLink = () => {
        const url = window.prompt("Link URL:", "https://");
        if (url) format("createLink", url);
    };

    const addButton = () => {
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0 && editorRef.current?.contains(sel.anchorNode)) {
            savedRangeRef.current = sel.getRangeAt(0).cloneRange();
        }
        editingBtnRef.current = null;
        setBtnLabel("Click Here");
        setBtnUrl("https://");
        setBtnSize("md");
        setBtnColor("#4f46e5");
        setBtnDialogOpen(true);
    };

    const handleEditorClick = (e: React.MouseEvent) => {
        const target = (e.target as HTMLElement).closest?.("a[data-btn]") as HTMLAnchorElement | null;
        if (!target || !editorRef.current?.contains(target)) return;
        e.preventDefault();
        editingBtnRef.current = target;
        setBtnLabel(target.textContent ?? "");
        setBtnUrl(target.getAttribute("href") ?? "https://");
        setBtnSize(target.getAttribute("data-size") ?? "md");
        setBtnColor(target.getAttribute("data-color") ?? "#4f46e5");
        setBtnDialogOpen(true);
    };

    const applyButtonDialog = () => {
        const btnText = btnLabel.trim() || "Click Here";
        const url = btnUrl.trim() || "#";
        if (editingBtnRef.current) {
            const el = editingBtnRef.current;
            el.textContent = btnText;
            el.setAttribute("href", url);
            el.setAttribute("data-size", btnSize);
            el.setAttribute("data-color", btnColor);
            el.setAttribute("style", buttonStyle(btnSize, btnColor));
        } else {
            editorRef.current?.focus();
            const sel = window.getSelection();
            if (savedRangeRef.current && sel) {
                sel.removeAllRanges();
                sel.addRange(savedRangeRef.current);
            }
            document.execCommand("insertHTML", false, buttonHtml(btnText, url, btnSize, btnColor));
            savedRangeRef.current = null;
        }
        setBtnDialogOpen(false);
        editingBtnRef.current = null;
        syncBody();
    };

    const removeButton = () => {
        editingBtnRef.current?.remove();
        editingBtnRef.current = null;
        setBtnDialogOpen(false);
        syncBody();
    };

    const addImage = () => {
        const url = window.prompt("Image URL:", "https://");
        if (!url) return;
        insertHtmlAtCaret(
            `<img src="${url}" alt="" style="max-width:100%;height:auto;border-radius:6px;" />`
        );
    };

    const insertVariable = (name: string, fromAtMenu = false) => {
        if (!fromAtMenu && lastFocused === "subject") {
            const el = subjectRef.current;
            const pos = el?.selectionStart ?? subject.length;
            setSubject((s) => s.slice(0, pos) + `{{.${name}}}` + s.slice(pos));
            return;
        }
        editorRef.current?.focus();
        if (fromAtMenu) {
            document.execCommand("delete", false);
        }
        document.execCommand("insertHTML", false, pillHtml(name, pillLabels[name] ?? name) + "&nbsp;");
        setAtMenu(null);
        syncBody();
    };

    const handleSave = async () => {
        const plainText = editorRef.current?.innerText?.trim() ?? "";
        if (!subject.trim()) return apiService.showSnack("Subject is required", "error");
        if (!plainText) return apiService.showSnack("Email body is required", "error");
        const html = pillsToTokens(editorRef.current?.innerHTML ?? body);
        const used = [...html.matchAll(/\{\{\.(\w+)\}\}/g), ...subject.matchAll(/\{\{\.(\w+)\}\}/g)].map((m) => m[1]);
        const allowed = new Set(variables.map((v) => v.name));
        const notAllowed = [...new Set(used)].filter((u) => !allowed.has(u));
        if (notAllowed.length > 0) {
            return apiService.showSnack(
                `These variables aren't available for this template: ${notAllowed.join(", ")}`,
                "error"
            );
        }

        setSaving(true);
        const payload = {
            project_key: projectKey,
            type,
            subject: subject.trim(),
            html_body: html,
            variables,
        };
        try {
            await apiService.put("/notification/email-template", payload);
            setIsCustom(true);
            apiService.showSnack("Template saved", "success");
            navigate(templatesPath, { state: { projectName } });
        } catch (error: any) {
            apiService.showSnack(
                error?.response?.data?.message || "Failed to save template",
                "error"
            );
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        confirmDialog(
            "confirm",
            `Reset the ${label || "this"} template for ${projectName} back to the platform default? Your customizations will be discarded.`
        ).then(() => {
            apiService.deleteData(
                `/notification/email-template?project_key=${encodeURIComponent(projectKey)}&type=${encodeURIComponent(type)}`,
                {
                    onSuccess: () => {
                        loadTemplate();
                        apiService.showSnack("Reset to default", "success");
                    },
                    onError: (err: any) => {
                        apiService.showSnack(
                            err?.response?.data?.message || "Failed to reset template",
                            "error"
                        );
                    },
                }
            )();
        });
    };

    const handleTestSend = () => {
        apiService.showSnack("Test email sent with sample data (simulated)", "success");
    };

    const toolbarButtons = [
        { title: "Bold", icon: <FormatBold fontSize="small" />, action: () => format("bold") },
        { title: "Italic", icon: <FormatItalic fontSize="small" />, action: () => format("italic") },
        { title: "Underline", icon: <FormatUnderlined fontSize="small" />, action: () => format("underline") },
        { title: "Bulleted list", icon: <FormatListBulleted fontSize="small" />, action: () => format("insertUnorderedList") },
        { title: "Numbered list", icon: <FormatListNumbered fontSize="small" />, action: () => format("insertOrderedList") },
        { title: "Insert link", icon: <LinkIcon fontSize="small" />, action: addLink },
        { title: "Insert button", icon: <SmartButton fontSize="small" />, action: addButton },
        { title: "Insert image", icon: <ImageIcon fontSize="small" />, action: addImage },
        { title: "Clear formatting", icon: <FormatClear fontSize="small" />, action: () => format("removeFormat") },
    ];

    return (
        <Box>
            {/* Breadcrumb / header */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
                <IconButton
                    size="small"
                    onClick={() => navigate(templatesPath, { state: { projectName } })}
                    data-qa="btn-back"
                >
                    <FontAwesomeIcon icon={faArrowLeft} />
                </IconButton>
                <Typography variant="body2" color="text.secondary">
                    <Link
                        component="button"
                        underline="hover"
                        color="inherit"
                        onClick={() => navigate("/application-settings/messaging/email")}
                        sx={{ verticalAlign: "baseline" }}
                    >
                        Email configuration
                    </Link>
                    {"  ›  "}
                    <Link
                        component="button"
                        underline="hover"
                        color="inherit"
                        onClick={() => navigate(templatesPath, { state: { projectName } })}
                        sx={{ verticalAlign: "baseline" }}
                    >
                        {projectName}
                    </Link>
                    {"  ›  "}
                    <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>
                        {label || "Template"}
                    </Box>
                </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5, ml: 5.5 }}>
                <Typography variant="h6" fontWeight={600}>
                    {label ? `${label} template` : "Template"}
                </Typography>
                <Chip
                    size="small"
                    label={loading ? "…" : isCustom ? "Customized" : "Using default"}
                    color={isCustom ? "primary" : "default"}
                    variant={isCustom ? "filled" : "outlined"}
                    sx={{ fontWeight: 600 }}
                    data-qa="chip-template-state"
                />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, ml: 5.5, maxWidth: 760 }}>
                {description} Write it just like in Gmail — click a variable chip (or type{" "}
                <b>@</b>) to insert a value; it becomes a chip and is replaced with the real value when
                the email is sent.
            </Typography>

            <Grid container spacing={3}>
                {/* Left column — compose */}
                <Grid size={{ xs: 12, md: 7 }}>
                    {/* Variables */}
                    <Box
                        sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1.5,
                            px: 2,
                            py: 1.5,
                            mb: 2,
                            bgcolor: "#fafafa",
                        }}
                    >
                        <Box sx={{ mb: 1 }}>
                            <Typography variant="subtitle2" fontWeight={600}>
                                Variables
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Click to insert into the message (or the subject), or type <b>@</b> while writing.
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                            {insertableVars.map((v) => (
                                <Chip
                                    key={v.name}
                                    label={v.label || v.name}
                                    size="small"
                                    onClick={() => insertVariable(v.name)}
                                    icon={<AlternateEmail fontSize="small" />}
                                    variant="outlined"
                                    sx={{ bgcolor: "#fff", cursor: "pointer" }}
                                    data-qa={`chip-var-${v.name}`}
                                />
                            ))}
                        </Box>
                        {urlVars.length > 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                                Link variables ({urlVars.map((v) => v.label || v.name).join(", ")})
                                are used inside buttons — set a button's link to{" "}
                                <code>{"{{.ApproveUrl}}"}</code> etc.
                            </Typography>
                        )}
                    </Box>

                    <TextField
                        fullWidth size="small" label="Subject"
                        placeholder="e.g. Approval needed"
                        value={subject}
                        inputRef={subjectRef}
                        onFocus={() => setLastFocused("subject")}
                        onChange={(e) => setSubject(e.target.value)}
                        sx={{ mb: 2 }} data-qa="input-template-subject"
                    />

                    {/* Compose area — Gmail style */}
                    <Box ref={editorWrapRef} sx={{ position: "relative", border: "1px solid", borderColor: "divider", borderRadius: 1.5, overflow: "visible" }}>
                        <Box
                            sx={{
                                display: "flex",
                                gap: 0.25,
                                px: 1,
                                py: 0.5,
                                borderBottom: "1px solid",
                                borderColor: "divider",
                                bgcolor: "#fafafa",
                                flexWrap: "wrap",
                            }}
                        >
                            {toolbarButtons.map((btn) => (
                                <Tooltip key={btn.title} title={btn.title} arrow>
                                    <IconButton
                                        size="small"
                                        onMouseDown={(e) => e.preventDefault() /* keep text selection */}
                                        onClick={btn.action}
                                    >
                                        {btn.icon}
                                    </IconButton>
                                </Tooltip>
                            ))}
                        </Box>
                        <Box
                            ref={editorRef}
                            contentEditable
                            suppressContentEditableWarning
                            data-qa="input-template-body"
                            onInput={handleEditorInput}
                            onClick={handleEditorClick}
                            onFocus={() => setLastFocused("body")}
                            onKeyDown={(e) => {
                                if (e.key === "Escape") setAtMenu(null);
                            }}
                            onBlur={() => setTimeout(() => setAtMenu(null), 150)}
                            sx={{
                                minHeight: 300,
                                maxHeight: 480,
                                overflow: "auto",
                                p: 2,
                                outline: "none",
                                fontFamily: "Arial, sans-serif",
                                fontSize: 14,
                                lineHeight: 1.6,
                                "& p": { m: 0, mb: 0.5 },
                            }}
                        />

                        {/* @ autocomplete menu */}
                        {atMenu && (
                            <Paper
                                elevation={4}
                                sx={{ position: "absolute", top: atMenu.top, left: atMenu.left, zIndex: 10, minWidth: 200, maxHeight: 260, overflow: "auto" }}
                                onMouseDown={(e) => e.preventDefault() /* keep caret */}
                            >
                                {insertableVars.map((v) => (
                                    <MenuItem key={v.name} dense onClick={() => insertVariable(v.name, true)} data-qa={`at-menu-${v.name}`}>
                                        <AlternateEmail fontSize="small" sx={{ mr: 1, color: "primary.main" }} />
                                        {v.label || v.name}
                                    </MenuItem>
                                ))}
                            </Paper>
                        )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        Tip: click a variable chip or type @ to insert a value while writing.
                    </Typography>
                </Grid>

                {/* Right column — live preview */}
                <Grid size={{ xs: 12, md: 5 }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Live Preview</Typography>
                    <Box
                        sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 2,
                            bgcolor: "#f5f5f7",
                            p: 2,
                            position: "sticky",
                            top: 16,
                        }}
                    >
                        <Typography variant="caption" color="text.secondary">Subject</Typography>
                        <Typography variant="subtitle2" sx={{ mb: 2 }}>{preview.subject}</Typography>
                        <Box
                            sx={{
                                bgcolor: "#fff",
                                borderRadius: 1,
                                p: 2,
                                minHeight: 300,
                                overflow: "auto",
                                fontFamily: "Arial, sans-serif",
                                fontSize: 14,
                                lineHeight: 1.6,
                                "& p": { m: 0, mb: 0.5 },
                            }}
                            dangerouslySetInnerHTML={{ __html: preview.html }}
                        />
                    </Box>
                </Grid>
            </Grid>

            {/* Actions */}
            <Box sx={{ display: "flex", gap: 2, mt: 4, mb: 2, flexWrap: "wrap" }}>
                <ArtButton
                    variant="contained"
                    dataQa="btn-save-template"
                    label={saving ? "Saving..." : "Save"}
                    onClick={() => {
                        if (!saving) handleSave();
                    }}
                />
                <ArtButton
                    variant="outlined"
                    dataQa="btn-test-send-template"
                    label="Send Test to Me"
                    icon={faPaperPlane}
                    onClick={handleTestSend}
                />
                <Box sx={{ flexGrow: 1 }} />
                <ArtButton
                    variant="outlined"
                    color="error"
                    dataQa="btn-reset-template"
                    label="Reset to Default"
                    disabled={loading || !isCustom}
                    onClick={handleReset}
                />
            </Box>

            {/* Button insert/edit dialog */}
            <Dialog open={btnDialogOpen} onClose={() => setBtnDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>{editingBtnRef.current ? "Edit Button" : "Insert Button"}</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1 }}>
                        <ArtTextField
                            label="Button text"
                            value={btnLabel} onChange={(e: any) => setBtnLabel(e.target.value)}
                            dataQa="input-button-label"
                            marginBottom={16}
                        />
                    </Box>
                    <ArtTextField
                        label="Link URL"
                        value={btnUrl} onChange={(e: any) => setBtnUrl(e.target.value)}
                        dataQa="input-button-url"
                        marginBottom={16}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                        Size
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                        {Object.entries(BUTTON_SIZES).map(([key, s]) => (
                            <Button
                                key={key}
                                size="small"
                                variant={btnSize === key ? "contained" : "outlined"}
                                onClick={() => setBtnSize(key)}
                                sx={{ textTransform: "none" }}
                                data-qa={`btn-size-${key}`}
                            >
                                {s.label}
                            </Button>
                        ))}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                        Color
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, mb: 2, alignItems: "center" }}>
                        {BUTTON_COLORS.map((c) => (
                            <Tooltip key={c.value} title={c.label} arrow>
                                <Box
                                    onClick={() => setBtnColor(c.value)}
                                    data-qa={`btn-color-${c.label.toLowerCase()}`}
                                    sx={{
                                        width: 26,
                                        height: 26,
                                        borderRadius: "50%",
                                        bgcolor: c.value,
                                        cursor: "pointer",
                                        border: btnColor === c.value ? "3px solid" : "2px solid transparent",
                                        borderColor: btnColor === c.value ? "primary.light" : "transparent",
                                        boxShadow: btnColor === c.value ? 2 : 0,
                                    }}
                                />
                            </Tooltip>
                        ))}
                        <Tooltip title="Custom color" arrow>
                            <Box
                                component="input"
                                type="color"
                                value={btnColor}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBtnColor(e.target.value)}
                                data-qa="btn-color-custom"
                                sx={{
                                    width: 32,
                                    height: 30,
                                    p: 0,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 1,
                                    cursor: "pointer",
                                    bgcolor: "transparent",
                                }}
                            />
                        </Tooltip>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                        Preview
                    </Typography>
                    <Box dangerouslySetInnerHTML={{ __html: `<a style="${buttonStyle(btnSize, btnColor)}cursor:default;">${btnLabel.trim() || "Click Here"}</a>` }} />
                </DialogContent>
                <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
                    <Box sx={{ minWidth: 90 }}>
                        {editingBtnRef.current && (
                            <ArtButton
                                variant="outlined"
                                color="error"
                                label="Remove"
                                onClick={removeButton}
                                dataQa="btn-remove-button"
                            />
                        )}
                    </Box>
                    <Box sx={{ display: "flex", gap: "12px" }}>
                        <ArtButton
                            variant="outlined"
                            color="inherit"
                            label="Cancel"
                            onClick={() => setBtnDialogOpen(false)}
                            dataQa="btn-cancel-button"
                        />
                        <ArtButton
                            variant="contained"
                            label={editingBtnRef.current ? "Update" : "Insert"}
                            onClick={applyButtonDialog}
                            dataQa="btn-apply-button"
                        />
                    </Box>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default usePageWrapper(EmailTemplateEditor, {
    page_title: "Email Template",
});
